import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { ProvisionWizard } from './ProvisionWizard';
import { UnitLifecycleActions } from '../components/UnitLifecycleActions';
import { useAuth } from '../hooks/useAuth';
import { SUPPORT_EMAIL } from '../config/support';
import { api } from '../utils/api';
import type { PublicDeviceStatus } from '../types';
import type { Site, Unit } from '../hooks/useDashboardData';
import './DeviceLanding.css';

export function DeviceLanding() {
  const { serial = '' } = useParams<{ serial: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [status, setStatus] = useState<PublicDeviceStatus | null>(null);
  const [error, setError] = useState('');
  const [sites, setSites] = useState<Site[]>([]);
  const [managedUnit, setManagedUnit] = useState<Unit | null>(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    api.get(`/api/v1/devices/${encodeURIComponent(serial)}/public-status`)
      .then(setStatus)
      .catch((loadError) => setError(loadError instanceof Error ? loadError.message : 'Could not find this device'));
  }, [refresh, serial]);

  useEffect(() => {
    if (!isAuthenticated || status?.lifecycleStatus !== 'adopted') return;
    let cancelled = false;
    const resolveManageTarget = async () => {
      const siteRows: Array<Site & { PK?: string; siteId?: string }> = await api.get('/api/v1/sites', { headers: { 'X-Tenant-ID': '' } });
      const accessibleSites = siteRows.map((site) => ({
        ...site,
        id: site.id || site.siteId || site.PK?.replace('SITE#', '') || '',
      })).filter((site) => site.id);
      const unitGroups: Unit[][] = await Promise.all(accessibleSites.map((site) =>
        api.get(`/api/v1/sites/${site.id}/units`, { headers: { 'X-Tenant-ID': '' } })
          .then((rows: Array<Unit & { SK?: string }>) => rows.map((unit) => ({
            ...unit,
            unitId: unit.unitId || unit.bindingId || unit.SK?.replace('UNIT#', '') || '',
            siteId: unit.siteId || site.id,
          })))
          .catch(() => []),
      ));
      let deviceUuid = localStorage.getItem(`rtm-device-uuid:${serial}`) ?? '';
      try {
        const devices = await api.get('/api/v1/devices');
        deviceUuid = devices.find((device: { serialNumber: string }) => device.serialNumber === serial)?.deviceUuid ?? '';
      } catch { /* list is super-admin only */ }
      const match = unitGroups.flat().find((unit) => unit.serialNumber === serial || (deviceUuid && unit.deviceUuid === deviceUuid));
      if (!cancelled) {
        setSites(accessibleSites);
        setManagedUnit(match ?? null);
      }
    };
    resolveManageTarget().catch(() => { if (!cancelled) setManagedUnit(null); });
    return () => { cancelled = true; };
  }, [isAuthenticated, refresh, serial, status?.lifecycleStatus]);

  if (error) return <div className="device-landing"><div className="device-landing__card"><Logo /><h1>Device not found</h1><p>{error}</p></div></div>;
  if (!status || authLoading) return <div className="device-landing"><p>Checking device…</p></div>;
  if (status.adoptable) return <ProvisionWizard serialNumber={serial} publicStatus={status} />;

  return (
    <div className="device-landing"><div className="device-landing__card">
      <Logo />
      <span className="device-landing__serial">{serial}</span>
      <h1>{status.lifecycleStatus === 'adopted' ? 'This device belongs to a site' : 'Device unavailable'}</h1>
      {status.lifecycleStatus === 'adopted' ? (
        <>
          <p>To transfer it, a site administrator must release it first. Releasing stops new publishing but preserves the site's existing telemetry history.</p>
          {!isAuthenticated ? (
            <Link className="device-landing__primary" to={`/login?returnTo=${encodeURIComponent(`/d/${serial}`)}`}>Sign in to manage</Link>
          ) : managedUnit ? (
            <UnitLifecycleActions unit={managedUnit} sites={sites} onComplete={() => setRefresh((value) => value + 1)} />
          ) : (
            <div className="device-landing__notice">
              <p>Open Sites and select the unit to use its release or move controls. Only an administrator of the bound site can perform those actions.</p>
              <Link to="/dashboard/sites">Manage site devices</Link>
            </div>
          )}
        </>
      ) : (
        <p>This lifecycle state cannot be adopted. Contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> if this is unexpected.</p>
      )}
    </div></div>
  );
}

export default DeviceLanding;
